<script setup>
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue';

// Props 정의
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    allowZero: {
        // true인 경우 값이 0 이여도 값으로 인정
        type: Boolean,
        default: false
    },
    inputId: {
        type: String,
        default: ''
    },
    checkId: {
        type: String,
        default: ''
    },
    checkLabel: {
        type: String,
        default: ''
    },
    checked: {
        type: Boolean,
        default: false
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    unit: {
        type: String,
        default: ''
    },
    number: {
        /* n0, n , n1, n2, n3 */
        type: String,
        default: ''
    },
    mask: {
        /* phone, business, time, password, license, resident */
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    padding: {
        type: String,
        default: ''
    },
    width: {
        type: String,
        default: ''
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);

const isReadonly = computed(() => props.readonly);
const isButtonName = computed(() => (props.button === '주소검색' ? '주소검색' : props.button));
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isChecked = ref(props.checked);
const showPassword = ref(false);
const isVisible = computed(() => props.visible);
const kakaoMapInstance = inject('kakaoMapInstance');

onMounted(() => {
    // const script = document.createElement('script');
    // script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    // document.head.appendChild(script);
    if (props.mask === 'time') {
        emitUpdate(inputValue.value, 'input');
    }
    // else {
    //     inputValue.value = '';
    //     emitUpdate(inputValue.value, 'input');
    // }

    // else if (props.mask === 'phone') {
    //     inputValue.value = '010-';
    // }
});

const isEmpty = (v) => {
    // null/undefined는 무조건 empty
    if (v === null || v === undefined) return true;

    // allowZero=false면 0도 empty 취급할지 선택 가능
    if (!props.allowZero && (v === 0 || v === '0')) return true;

    // 문자열 공백/빈문자
    if (typeof v === 'string' && v.trim() === '') return true;

    return false;
};

const hasValue = computed(() => {
    if (inputValue.value === null || inputValue.value === undefined) return false;
    if (props.allowZero) return inputValue.value !== '';
    return Boolean(inputValue.value);
});

const formatTime = (value) => {
    const cleanedValue = value.replace(/\D/g, '').slice(0, 4); // 숫자만 허용하고 최대 길이 4
    console.log('cleanedValue: ', cleanedValue);
    if (cleanedValue.length >= 3) {
        let hours = cleanedValue.slice(0, 2);
        let minutes = cleanedValue.slice(2);

        // 시간이 24 이상이면 23으로 제한
        hours = Math.min(parseInt(hours, 10), 23).toString().padStart(2, '0');

        // 분이 60 이상이면 59로 제한
        minutes = Math.min(parseInt(minutes, 10), 59).toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    } else if (cleanedValue.length === 2) {
        // 두 자리 숫자일 때 시간으로 처리
        return `${cleanedValue}:00`;
    } else if (cleanedValue.length === 1) {
        // 한 자리 숫자일 때 앞에 0을 붙임
        return `0${cleanedValue}`;
    }
    return cleanedValue;
};
const formatTime2 = (value) => {
    const cleanedValue = value.replace(/\D/g, '').slice(0, 4); // 숫자만 허용하고 최대 길이 4
    //console.log(value.split(':')[0].length, cleanedValue);

    if (cleanedValue.length >= 3) {
        let hours = cleanedValue.slice(0, 2);
        let minutes = cleanedValue.slice(2);

        // 시간이 24 이상이면 23으로 제한
        hours = Math.min(parseInt(hours, 10), 23).toString().padStart(2, '0');

        // 분을 30분 단위로 제한 (0 또는 30으로 설정)
        minutes = Math.min(parseInt(minutes, 10), 59);
        minutes = minutes < 30 ? 0 : 30; // 30분 미만은 0, 30분 이상은 30으로 설정
        minutes = minutes.toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    } else if (cleanedValue.length === 2) {
        // 두 자리 숫자일 때 시간으로 처리하고 분은 00으로 설정
        return `${cleanedValue}:00`;
    } else if (cleanedValue.length === 1) {
        // 한 자리 숫자일 때 앞에 0을 붙이지 않고 분은 00으로 설정
        return `${cleanedValue}:00`;
    }
    return cleanedValue;
};

const formatPhoneNumber = (value) => {
    if (!value) {
        return;
    }
    if (value.length > 0 && value[0] !== '0') {
        ValidationMsg.value = '올바른 전화번호 형식이 아닙니다. ( 0부터 입력 )';
        return '';
    }

    if (!/^[0-9.-]*$/.test(value)) {
        ValidationMsg.value = value.substring(value.length - 1) + ' 는 올바른 전화번호 형식이 아닙니다.';
    }

    value = value.replace(/[^0-9]/g, '');

    // 휴대폰 번호 (010으로 시작, 3-4-4 형식)
    if (value.startsWith('010')) {
        if (value.length <= 3) return value;
        if (value.length <= 7) return value.slice(0, 3) + '-' + value.slice(3);
        return value.slice(0, 3) + '-' + value.slice(3, 7) + '-' + value.slice(7, 11);
    }

    // 서울 지역 번호 (02로 시작, 2-3-4 또는 2-4-4 형식)
    if (value.startsWith('02')) {
        if (value.length <= 2) return value;
        if (value.length <= 5) return value.slice(0, 2) + '-' + value.slice(2);
        if (value.length <= 9) return value.slice(0, 2) + '-' + value.slice(2, 5) + '-' + value.slice(5);
        return value.slice(0, 2) + '-' + value.slice(2, 6) + '-' + value.slice(6, 10);
    }

    // 기타 지역 번호 (0으로 시작, 3-3-4 또는 3-4-4 형식)
    if (value.startsWith('0')) {
        if (value.length > 12) value = value.slice(0, 12);
        if (value.length <= 3) return value;
        if (value.length <= 6) return value.slice(0, 3) + '-' + value.slice(3, 6);
        if (value.length <= 9) return value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6);
        if (value.length === 10) return value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
        if (value.length === 11) return value.slice(0, 3) + '-' + value.slice(3, 7) + '-' + value.slice(7, 11);
        if (value.length === 12) return value.slice(0, 4) + '-' + value.slice(4, 8) + '-' + value.slice(8, 12);
        return value.slice(0, 3) + '-' + value.slice(3, 7) + '-' + value.slice(7, 11);
    }

    // 기본 반환 (규칙에 맞지 않는 경우)
    return value;
};

// const formatPhoneNumber = (value) => {
//     value = value.replace(/[^0-9]/g, '');
//     if (value.length <= 3) {
//         return '010-';
//     } else {
//         let formattedValue = '010-';
//         if (value.length > 3) {
//             formattedValue += value.slice(3, 7);
//         }
//         if (value.length > 7) {
//             formattedValue += '-' + value.slice(7, 11);
//         }
//         return formattedValue;
//     }
// };

const isValidTime = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60;
};

const formatBusinessNumber = (value) => {
    value = value.replace(/[^0-9]/g, '');

    if (value.length <= 3) {
        return value;
    } else if (value.length <= 5) {
        return value.slice(0, 3) + '-' + value.slice(3);
    } else {
        return value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5, 10);
    }
};

const formatResidentNumber = (value) => {
    value = value.replace(/[^0-9]/g, '');

    if (value.length <= 6) {
        return value;
    } else {
        return value.slice(0, 6) + '-' + value.slice(6, 13);
    }
};

const formatLicenseNumber = (value) => {
    if (!value) {
        return '';
    }
    value = value.replace(/[^0-9]/g, '');

    if (value.length <= 2) {
        return value;
    } else if (value.length <= 4) {
        return value.slice(0, 2) + '-' + value.slice(2);
    } else if (value.length <= 10) {
        return value.slice(0, 2) + '-' + value.slice(2, 4) + '-' + value.slice(4, 10);
    } else {
        return value.slice(0, 2) + '-' + value.slice(2, 4) + '-' + value.slice(4, 10) + '-' + value.slice(10, 12);
    }
};

const formatNumber = (value, numberType) => {
    //if (value === null) return '';
    if (value === null || value === undefined || value === '') {
        return '';
    }

    value = value.toString().replace(/[^0-9.]/g, ''); // value = value.toString().replace(/[^0-9.-]/g, '');
    let maxDecimalPlaces;
    let isNegative = false;

    // 음수 처리
    if (value.startsWith('-')) {
        isNegative = true;
        value = value.slice(1);
    }

    if (numberType === 'n' || numberType === 'n0') {
        value = value.replace(/\./g, '');
    } else if (['n1', 'n2', 'n3'].includes(numberType)) {
        maxDecimalPlaces = parseInt(numberType.slice(1));
        const parts = value.split('.');
        if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('');
        }
        if (maxDecimalPlaces && parts[1] && parts[1].length > maxDecimalPlaces) {
            parts[1] = parts[1].slice(0, maxDecimalPlaces);
            value = parts.join('.');
        }
    } else if (['d'].includes(numberType)) {
        const parts = value.split('.');
        if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('');
        }
    }

    // 정수 부분과 소수 부분 분리
    const [integerPart, decimalPart] = value.split('.');

    // 정수 부분에 3자리마다 쉼표 추가
    let formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // 앞에 불필요한 0 제거
    formattedIntegerPart = formattedIntegerPart.replace(/^0+/, '');
    if (formattedIntegerPart === '') formattedIntegerPart = '0';

    // 결과 조합
    let result = formattedIntegerPart;
    if (decimalPart !== undefined) {
        result += '.' + decimalPart;
    }

    // 음수 부호 추가
    if (isNegative) {
        result = '-' + result;
    }

    // '0.'으로 시작하는 경우 처리
    if (result.startsWith('.')) {
        result = '0' + result;
    }

    if (numberType === 'n0') result = result.replaceAll(',', '');

    return result;
};

watch(
    () => props.modelValue,
    (newVal) => {
        // if (newVal instanceof Object) {
        if (props.upper) {
            newVal = newVal.toUpperCase();
        }

        const baseVal = isEmpty(newVal) ? '' : newVal;

        if (props.mask === 'phone') {
            inputValue.value = formatPhoneNumber(String(baseVal)) || '';
        } else if (props.mask === 'business') {
            inputValue.value = formatBusinessNumber(String(baseVal)) || '';
        } else if (props.mask === 'license') {
            inputValue.value = formatLicenseNumber(String(baseVal)) || '';
        } else if (props.number) {
            inputValue.value = formatNumber(baseVal, props.number.toLowerCase()) || '';
        } else if (props.mask === 'resident') {
            inputValue.value = formatResidentNumber(String(baseVal)) || '';
        } else {
            inputValue.value = baseVal;
        }

        // let emitValue = inputValue.value;
        // if (props.mask === 'phone' || props.mask === 'business') {
        //     emitValue = emitValue.replaceAll('-', '');
        // } else if (props.number) {
        //     emitValue = emitValue.replaceAll(',', '');
        // }
        // emit('update:modelValue', emitValue);
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

watch(
    () => props.checked,
    (newVal) => {
        isChecked.value = newVal;
        //if (typeof newVal === 'boolean') isChecked.value = newVal;
    },
    { immediate: true, deep: true }
);

const onInput = (event) => {
    const oldValue = event.target.value;
    const oldLength = oldValue.length;
    let cursorPosition = event.target.selectionStart;
    let value = event.target.value;
    let validationResult = { isValid: true, message: '' };
    ValidationMsg.value = '';
    errMessage.value = '';

    if (value.length > props.maxlength) {
        value = value.slice(0, props.maxlength);
    }

    const beforeFormatLength = value.length;

    if (props.mask && props.mask === 'phone') {
        const formattedValue = formatPhoneNumber(value);
        const hyphenPositions = [];
        for (let i = 0; i < formattedValue.length; i++) {
            if (formattedValue[i] === '-') {
                hyphenPositions.push(i);
            }
        }
        // console.log(oldLength, cursorPosition, formattedValue, hyphenPositions);
        if (hyphenPositions.length > 0 && cursorPosition === hyphenPositions[0] + 1) {
            cursorPosition = cursorPosition + 1;
        }

        if (hyphenPositions.length > 1 && cursorPosition === hyphenPositions[1] + 1) {
            cursorPosition = cursorPosition + 1;
        }

        value = formattedValue;
    } else if (props.mask && props.mask === 'business') {
        const formattedValue = formatBusinessNumber(value);
        const hyphenPositions = [3, 6].filter((pos) => formattedValue[pos] === '-');
        //console.log(oldLength, cursorPosition, formattedValue, hyphenPositions);
        if (hyphenPositions.length > 0 && cursorPosition === hyphenPositions[0] + 1) {
            cursorPosition = cursorPosition + 1;
        }

        if (hyphenPositions.length > 1 && cursorPosition === hyphenPositions[1] + 1) {
            cursorPosition = cursorPosition + 1;
        }

        value = formattedValue;
    } else if (props.mask && props.mask === 'resident') {
        const formattedValue = formatResidentNumber(value);
        const hyphenPositions = [6].filter((pos) => formattedValue[pos] === '-');
        //console.log(oldLength, cursorPosition, formattedValue, hyphenPositions);
        if (hyphenPositions.length > 0 && cursorPosition === hyphenPositions[0] + 1) {
            cursorPosition = cursorPosition + 1;
        }

        value = formattedValue;
    } else if (props.mask && props.mask === 'license') {
        const formattedValue = formatLicenseNumber(value);
        const hyphenPositions = [2, 5, 12].filter((pos) => formattedValue[pos] === '-');

        if (hyphenPositions.length > 0 && cursorPosition === hyphenPositions[0] + 1) {
            cursorPosition = cursorPosition + 1;
        }

        if (hyphenPositions.length > 1 && cursorPosition === hyphenPositions[1] + 1) {
            cursorPosition = cursorPosition + 1;
        }

        if (hyphenPositions.length > 2 && cursorPosition === hyphenPositions[2] + 1) {
            cursorPosition = cursorPosition + 1;
        }

        value = formattedValue;
    } else if (props.mask && props.mask === 'time') {
        const formattedValue = formatTime(value);
        const hyphenPositions = [2].filter((pos) => formattedValue[pos] === ':');
        //console.log(oldLength, cursorPosition, formattedValue, hyphenPositions);
        if (hyphenPositions.length > 0 && cursorPosition === hyphenPositions[0] + 1) {
            cursorPosition = cursorPosition + 1;
        }

        // const newLength = formattedValue.length;

        // if (oldLength === 3 && newLength === 4 && cursorPosition === 3) {
        //     cursorPosition = 4;
        // } else if (oldLength >= 5 && newLength === 5 && cursorPosition === 3) {
        //     cursorPosition = 4;
        // }
        value = formattedValue;
    } else if (props.mask && props.mask === 'time2') {
        const formattedValue = formatTime2(value);
        const hyphenPositions = [2].filter((pos) => formattedValue[pos] === ':');

        // 커서 위치 조정
        if (hyphenPositions.length > 0 && cursorPosition === hyphenPositions[0] + 1) {
            cursorPosition = cursorPosition + 1;
        }

        value = formattedValue;
    } else if (props.number) {
        const rawValue = value || '';
        const formattedValue = formatNumber(rawValue, props.number.toLowerCase()) || '';

        // 커서 위치 조정
        let commaCountBefore = (rawValue.match(/,/g) || []).length;
        let commaCountAfter = (formattedValue.match(/,/g) || []).length;
        let diff = commaCountAfter - commaCountBefore;

        cursorPosition += diff;

        value = formattedValue;
    }

    if (props.upper) {
        value = value.toUpperCase();
    }

    const afterFormatLength = value.length;
    const lengthDiff = afterFormatLength - beforeFormatLength;

    // 커서 위치 조정
    // if (lengthDiff !== 0) {
    //     cursorPosition = cursorPosition + lengthDiff;
    // }

    // 커서가 값의 길이를 초과하지 않도록 보장
    cursorPosition = Math.min(cursorPosition, afterFormatLength);

    if (typeof props.validationCheck === 'function') {
        try {
            validationResult = props.validationCheck(value) || validationResult;
        } catch (error) {
            console.error('Validation check error:', error);
        }
    }

    ValidationMsg.value = validationResult.isValid ? ValidationMsg.value : validationResult.message;
    emitUpdate(value, 'input');

    nextTick(() => {
        event.target.value = value;
        event.target.setSelectionRange(cursorPosition, cursorPosition);
    });
};

const emitUpdate = (value, event) => {
    if (props.mask === 'phone' || props.mask === 'business') value = value.replaceAll('-', '');
    if (props.number) value = value.replaceAll(',', '');
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        event: event,
        value: value,
        checked: isChecked.value,
        validation: ValidationMsg.value === '' ? true : false
    });
};

const onButtonClick = (event) => {
    if (!props.readonly) {
        if (props.button === '주소검색') {
            execDaumPostcode();
        } else {
            emitUpdate(inputValue.value, 'click');
        }
    }
};

const onCheckboxClick = (event) => {
    if (!props.readonly) {
        isChecked.value = event.target.checked;
        emitUpdate(inputValue.value, 'check');
    }
};

const execDaumPostcode = () => {
    if (window.daum && window.daum.Postcode) {
        new window.daum.Postcode({
            oncomplete: function (data) {
                let addr = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
                let extraAddr = '';

                if (data.userSelectedType === 'R') {
                    if (data.bname !== '' && /[동|로|가]$/g.test(data.bname)) {
                        extraAddr += data.bname;
                    }
                    if (data.buildingName !== '' && data.apartment === 'Y') {
                        extraAddr += extraAddr !== '' ? ', ' + data.buildingName : data.buildingName;
                    }
                    if (extraAddr !== '') {
                        extraAddr = ' (' + extraAddr + ')';
                    }
                }

                /*
                추가데이터
                postcode: data.zonecode,   우편번호
                extraAddress: extraAddr    추가주소
                roadAddress
                */

                emitUpdate(addr, 'address');
                ValidationMsg.value = '';
                errMessage.value = '';
            }
        }).open();
    } else {
        console.error('Daum Postcode script not loaded');
    }
};

const clearInput = () => {
    ValidationMsg.value = '';
    errMessage.value = '';

    emitUpdate('', 'input');
};

const togglePasswordVisibility = () => {
    showPassword.value = !showPassword.value;
};

const inputType = computed(() => {
    if (props.mask === 'password') {
        return showPassword.value ? 'text' : 'password';
    }
    return 'text';
});
</script>

<template>
    <!-- {{ isReadonly }} -->
    <div
        v-if="isVisible"
        class="flex flex-col w-full"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="props.label.toLowerCase() !== 'empty' && !labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message" :title="ValidationMsg">{{ ValidationMsg }}</span>

            <div v-if="checkId" class="checkbox-container">
                <label :for="checkId" class="checkbox-label">
                    {{ checkLabel }}
                </label>
                <input type="checkbox" :id="checkId" class="checkbox-input" :disabled="isReadonly" v-model="isChecked" @click="onCheckboxClick" />
            </div>
        </label>
        <div class="relative flex items-center h-12 w-full">
            <input
                v-if="isVisible"
                :type="inputType"
                :id="inputId"
                v-model="inputValue"
                class="p-inputtext block h-[2.5rem] ps-5 w-full input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                :class="{
                    'pr-24': button && !isReadonly,
                    'pr-24': props.mask === 'password',
                    'pr-unit': unit && !button && props.mask !== 'password',
                    'error-border': ValidationMsg,
                    'pr-[10]': props.number && !button && !unit && props.mask !== 'password',
                    'readonly-Bg': isReadonly
                }"
                :placeholder="placeholder !== '' ? placeholder : props.button === '주소검색' ? '주소를 입력 하세요.' : props.mask === 'phone' ? '숫자만 입력 하세요.' : placeholder"
                :required="required"
                :readonly="isReadonly"
                @input="onInput"
                @keyup.enter="onEnter"
                @click="onClick"
                @dblclick="onDblClick"
            />

            <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center">
                <button
                    v-if="hasValue && !isReadonly"
                    @click="clearInput"
                    class="clear-button"
                    :class="{
                        'mr-[calc(1.1rem)]': props.mask === 'password',
                        'mr-[calc(1rem)]': unit,
                        'mr-[calc(6.5rem)]': button,
                        'mr-4': !button && !unit && props.mask !== 'password'
                    }"
                >
                    &times;
                </button>

                <button v-if="props.mask === 'password'" type="button" class="password-toggle" @click="togglePasswordVisibility">
                    <i :class="['pi', showPassword ? 'pi-eye' : 'pi-eye-slash']"></i>
                </button>

                <span v-if="unit" class="unit-text">{{ unit }}</span>

                <button v-if="button && !isReadonly" @click="onButtonClick" class="button-style">{{ isButtonName }}</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
    white-space: nowrap;
    user-select: none;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus:not([readonly]) {
    border-color: #0038ea;
    outline: none;
}

input[readonly]:focus {
    border-color: #d1d5db;
    outline: none;
}

.text-right {
    text-align: right;
}

.error-border {
    border: 1px solid red !important;
}

.readonly-Bg {
    background-color: whitesmoke !important;
}

.custom-input.p-inputtext {
    /* padding: 0.75rem; 기본 높이 */
    border: 1px solid #d1d5db; /* 기본 테두리 색상 */
    border-radius: 0.5rem; /* 기본 border-radius */
    /* transition:
        border-color 0.3s ease,
        box-shadow 0.3s ease,
        height 0.3s ease; 애니메이션 */
}

.custom-input.p-inputtext:focus:not([readonly]) {
    border-color: #0038ea !important;
}

.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 5.5rem;
    height: 1.8rem;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.checkbox-container {
    display: flex;
    align-items: center;
    margin-left: auto;
}

.checkbox-input {
    width: 1rem;
    height: 1rem;
    margin-right: 0.1rem;
    background-color: #f3f4f6;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.25rem;
    cursor: pointer;
}

.checkbox-label {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    margin-right: 0.5rem;
}

.password-container {
    position: relative;
}

.password-input {
    padding-right: 2.5rem;
}

.password-toggle {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 1.2rem;
    color: #6b7280;
    padding: 0.25rem;
    width: 2.2rem;
    right: 2.5rem;
}

.password-toggle:hover {
    color: #4b5563;
}

.unit-text {
    position: absolute;
    right: 2.6rem;
    display: flex;
    align-items: center;
    height: 100%;
    font-family: 'SCoreDream500';
    font-size: 0.875rem;
    color: #6b7280;
    user-select: none;
    white-space: nowrap;
}
.pr-unit {
    padding-right: 5rem !important;
}
.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.875rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

input::-webkit-input-placeholder {
    text-align: left !important;
}
input::-moz-placeholder {
    /* Firefox 19+ */
    text-align: left !important;
}
input:-ms-input-placeholder {
    /* IE 10+ */
    text-align: left !important;
}
input:-moz-placeholder {
    /* Firefox 18- */
    text-align: left !important;
}

.text-right::-webkit-input-placeholder {
    text-align: left !important;
}
.text-right::-moz-placeholder {
    text-align: left !important;
}
.text-right:-ms-input-placeholder {
    text-align: left !important;
}
.text-right:-moz-placeholder {
    text-align: left !important;
}
</style>
